import React, { useState } from "react";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * QrTokenRevokeDialog — Sub-sprint C.
 *
 * Confirmation modal with REQUIRED `reason` textarea. Calls
 * `onConfirm(reason)` and closes on success. The hard-delete endpoint
 * is intentionally NOT wired; only PATCH /revoke is exposed.
 */
export const QrTokenRevokeDialog = ({ trigger, onConfirm, disabled = false }) => {
  const t = useRacketLabAdminT();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const triggerWithClick = React.cloneElement(trigger, {
    onClick: (e) => { e.preventDefault(); if (!disabled) setOpen(true); },
    "data-testid": "rl-qr-revoke-trigger",
  });

  if (!open) return triggerWithClick;

  const trimmed = reason.trim();
  const valid = trimmed.length >= 3;

  const handleConfirm = async () => {
    if (!valid) return;
    try { await onConfirm(trimmed); setOpen(false); setReason(""); } catch { /* caller toast */ }
  };

  return (
    <>
      {triggerWithClick}
      <div
        data-testid="rl-qr-revoke-modal"
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
        onClick={() => setOpen(false)}
      >
        <div className="w-full max-w-md rounded-md bg-white p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-base font-semibold text-slate-900">{t("qr_tokens.revoke.title")}</h3>
          <p className="mt-1 text-xs text-slate-500">
            {t("qr_tokens.revoke.subtitle")}
          </p>
          <label className="mt-3 flex flex-col gap-1 text-xs">
            <span className="font-medium text-slate-700">{t("qr_tokens.revoke.reason_label_required")}</span>
            <textarea
              data-testid="rl-qr-revoke-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              data-testid="rl-qr-revoke-cancel"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
              onClick={() => { setOpen(false); setReason(""); }}
            >{t("qr_tokens.revoke.cancel_label")}</button>
            <button
              type="button"
              data-testid="rl-qr-revoke-confirm-btn"
              disabled={!valid}
              className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${valid ? "bg-rose-600 hover:bg-rose-700" : "bg-rose-300 cursor-not-allowed"}`}
              onClick={handleConfirm}
            >{t("qr_tokens.revoke.submit")}</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default QrTokenRevokeDialog;
